import {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Link, useSearchParams} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {HelpIcon} from '@/components/help-icon'
import {PorQue} from '@/components/ui/por-que'
import {ListEmptyState} from '@/components/section-card'
import {DataTable} from '@/components/data-table'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {GroupedMatriz, type GroupMode} from '@/components/ipevr/grouped-matriz'
import {coincideConFiltrosDePersonas, opcionesDeCargo} from '@/components/ipevr/filtros-matriz'
import {AddFilaDialog} from '@/components/ipevr/add-fila-dialog'
import {ImportarMatrizDialog} from '@/components/ipevr/importar-matriz-dialog'
import {EditFilaDialog} from '@/components/ipevr/edit-fila-dialog'
import {FilaDetailsDialog} from '@/components/ipevr/fila-details-dialog'
import {MapaCalorView} from '@/components/ipevr/mapa-calor'
import {OtraMetodologiaPanel} from '@/components/ipevr/otra-metodologia-panel'
import {FilaCard, buildColumns} from '@/components/matriz-riesgos-columns'
import {MatrizRiesgosToolbar, type EstadoFilter} from '@/components/matriz-riesgos-toolbar'
import {PageNotices} from '@/components/page-notices'
import {useReSolicitarTodas} from '@/hooks/sgsst/riesgos/use-entrevista'
import {useHighlightParam} from '@/hooks/common/use-highlight-param'
import {
  gateBlockedCopy,
  useMarkRevision,
  useMarkVigente,
  useSetMetodologia,
  type Fila,
  type Matriz,
  type RiesgoFilaClasificacion,
  type RiesgoFilaInterpretacion,
} from '@/hooks/sgsst/riesgos/use-ipevr'
import {formatDateOnly, localToday} from '@/lib/risk'
import {publicarExito} from '@/lib/avisos'
import {translateApiError} from '@/lib/errors'
import {cn} from '@/lib/utils'

const INTERPRETACION_SEVERITY: Record<string, number> = {I: 0, II: 1, III: 2, IV: 3}
type SortBy = 'descripcion' | 'clasificacion' | 'interpretacion' | 'estado' | 'num_expuestos'

// «mapa» is not a grouping of the same rows: it is aggregated server-side with its own filters.
type VistaMode = GroupMode | 'mapa'
const VISTAS: VistaMode[] = ['none', 'cargo', 'proceso', 'area', 'mapa']

// The matriz arrives whole in one fetch, so pagination slices in memory.
const PAGE_SIZE = 15

function sortFilas(filas: Fila[], sortBy: SortBy, dir: 'asc' | 'desc', noAceptablePrimero: boolean): Fila[] {
  const sorted = [...filas]
  const sign = dir === 'asc' ? 1 : -1
  sorted.sort((a, b) => {
    let cmp = 0
    if (sortBy === 'descripcion') cmp = a.descripcion.localeCompare(b.descripcion)
    else if (sortBy === 'clasificacion') cmp = a.clasificacion.localeCompare(b.clasificacion)
    else if (sortBy === 'interpretacion')
      cmp =
        (INTERPRETACION_SEVERITY[a.interpretacion ?? ''] ?? 4) - (INTERPRETACION_SEVERITY[b.interpretacion ?? ''] ?? 4)
    else if (sortBy === 'estado') cmp = Number(a.confirmado_en === null) - Number(b.confirmado_en === null)
    else cmp = (a.num_expuestos ?? -1) - (b.num_expuestos ?? -1)
    return cmp * sign
  })
  // Stable sort: within a severity band the chosen column order survives.
  if (noAceptablePrimero) {
    sorted.sort(
      (a, b) =>
        (INTERPRETACION_SEVERITY[a.interpretacion ?? ''] ?? 4) - (INTERPRETACION_SEVERITY[b.interpretacion ?? ''] ?? 4)
    )
  }
  return sorted
}

function matchesSearch(fila: Fila, needle: string): boolean {
  if (!needle) return true
  return [fila.proceso, fila.zona, fila.actividad, fila.tarea, fila.descripcion].some((v) =>
    v?.toLowerCase().includes(needle)
  )
}

// Same «sin evidencia» rule as the FilaControl badge.
function filaHasControlSinEvidencia(fila: Fila): boolean {
  return fila.controles.some((c) => c.evidencia_hint && c.fotos.length === 0)
}

export function MatrizRiesgosGrid({
  businessId,
  matriz,
  canOperate,
  canEdit,
  isSystemUser,
}: {
  businessId: string
  matriz: Matriz
  canOperate: boolean
  canEdit: boolean
  isSystemUser: boolean
}) {
  const {t} = useTranslation()
  const [searchParams] = useSearchParams()
  const setMetodologia = useSetMetodologia(businessId)
  const markRevision = useMarkRevision(businessId)
  const markVigente = useMarkVigente(businessId)
  const reSolicitarTodas = useReSolicitarTodas(businessId)
  const [addingFila, setAddingFila] = useState(false)
  const [importando, setImportando] = useState(false)
  const [editingFila, setEditingFila] = useState<Fila | null>(null)
  const [detailsFila, setDetailsFila] = useState<Fila | null>(null)
  const [search, setSearch] = useState('')
  const [riskFilter, setRiskFilter] = useState<Set<RiesgoFilaInterpretacion>>(new Set())
  const [clasificacionFilter, setClasificacionFilter] = useState<Set<RiesgoFilaClasificacion>>(new Set())
  const [estadoFilter, setEstadoFilter] = useState<Set<EstadoFilter>>(new Set())
  const [empleadoFilter, setEmpleadoFilter] = useState<Set<string>>(new Set())
  const [cargoFilter, setCargoFilter] = useState<Set<string>>(new Set())
  const [areaFilter, setAreaFilter] = useState<Set<string>>(new Set())
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [noAceptablePrimero, setNoAceptablePrimero] = useState(false)
  const [groupMode, setGroupMode] = useState<VistaMode>('none')
  const [sinCalificarFilter, setSinCalificarFilter] = useState(false)
  const [sinEvidenciaFilter, setSinEvidenciaFilter] = useState(false)
  const [sortBy, setSortBy] = useState<SortBy>('descripcion')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [vigenteError, setVigenteError] = useState<string | null>(null)
  const [confirmingRevision, setConfirmingRevision] = useState(false)
  const [confirmingResolicitarTodas, setConfirmingResolicitarTodas] = useState(false)

  const filas = useMemo(() => matriz.filas, [matriz])
  const empleadoOptions = useMemo(() => {
    const byId = new Map<string, string>()
    for (const f of filas) for (const e of f.origen_empleados) byId.set(e.id, e.nombre_completo)
    return Array.from(byId, ([id, name]) => ({id, name})).sort((a, b) => a.name.localeCompare(b.name))
  }, [filas])
  const cargoOptions = useMemo(() => opcionesDeCargo(filas), [filas])
  const areaOptions = useMemo(() => {
    const names = new Set<string>()
    for (const f of filas) for (const e of f.origen_empleados) for (const a of e.areas) names.add(a)
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({id: name, name}))
  }, [filas])

  function updateSearch(value: string) {
    setSearch(value)
    setPage(1)
  }

  function toggleInSet<T>(setter: (updater: (prev: Set<T>) => Set<T>) => void, value: T) {
    setter((prev) => {
      const next = new Set(prev)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
    setPage(1)
  }

  function updateNoAceptablePrimero() {
    setNoAceptablePrimero((v) => !v)
    setPage(1)
  }

  function handleSort(key: string) {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortBy(key as SortBy)
      setSortDir('asc')
    }
    setPage(1)
  }

  function clearFilters() {
    setSearch('')
    setRiskFilter(new Set())
    setClasificacionFilter(new Set())
    setEstadoFilter(new Set())
    setEmpleadoFilter(new Set())
    setCargoFilter(new Set())
    setAreaFilter(new Set())
    setNoAceptablePrimero(false)
    setSinEvidenciaFilter(false)
    setSinCalificarFilter(false)
    setPage(1)
  }

  function toggleSinEvidenciaFilter() {
    setSinEvidenciaFilter((v) => !v)
    setPage(1)
  }

  function toggleSinCalificarFilter() {
    setSinCalificarFilter((v) => !v)
    setPage(1)
  }

  const hasActiveFilters =
    search.trim() !== '' ||
    riskFilter.size > 0 ||
    clasificacionFilter.size > 0 ||
    estadoFilter.size > 0 ||
    empleadoFilter.size > 0 ||
    cargoFilter.size > 0 ||
    areaFilter.size > 0 ||
    noAceptablePrimero ||
    sinEvidenciaFilter ||
    sinCalificarFilter
  const moreFiltersOpen = showMoreFilters || empleadoFilter.size > 0 || cargoFilter.size > 0 || areaFilter.size > 0

  const needle = search.trim().toLowerCase()
  const filasSinCalificar = useMemo(() => matriz.filas.filter((f) => f.interpretacion === null).length, [matriz.filas])
  const filtered = matriz.filas.filter(
    (f) =>
      matchesSearch(f, needle) &&
      (riskFilter.size === 0 || (f.interpretacion && riskFilter.has(f.interpretacion))) &&
      (clasificacionFilter.size === 0 || clasificacionFilter.has(f.clasificacion)) &&
      (estadoFilter.size === 0 || estadoFilter.has(f.confirmado_en === null ? 'sin_confirmar' : 'confirmada')) &&
      (!sinEvidenciaFilter || filaHasControlSinEvidencia(f)) &&
      (!sinCalificarFilter || f.interpretacion === null) &&
      coincideConFiltrosDePersonas(f, {empleados: empleadoFilter, cargos: cargoFilter, areas: areaFilter})
  )
  const visible = sortFilas(filtered, sortBy, sortDir, noAceptablePrimero)
  const paged = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const columns = buildColumns(t)

  // `?fila=<id>` desde el plan: la fila puede estar en otra página, así que se salta a ella.
  const filaBuscada = searchParams.get('fila')
  const indiceBuscado = filaBuscada ? visible.findIndex((f) => f.id === filaBuscada) : -1
  const paginaBuscada = indiceBuscado >= 0 ? Math.floor(indiceBuscado / PAGE_SIZE) + 1 : null
  useEffect(() => {
    if (paginaBuscada !== null) setPage(paginaBuscada)
  }, [paginaBuscada])
  useHighlightParam(
    'fila',
    paged.some((f) => f.id === filaBuscada)
  )

  return (
    <div className="space-y-5">
      {/* nosemgrep: browser-clock-business-date -- O7: la revisión anual vencida avisa fuerte, sin bloquear la operación; overdue determination off the browser clock, tracked debt, not fixed in this wave (FE-02) */}
      {matriz.proxima_revision && matriz.proxima_revision < localToday() && (
        <div
          className="tone-amber flex flex-wrap items-center gap-2 rounded-lg px-4 py-2.5 text-sm"
          data-testid="ipevr-revision-vencida">
          <span className="font-medium">{t('ipevr.revision.overdue')}</span>
          <span className="text-xs">
            {t('ipevr.revision.overdueSince', {date: formatDateOnly(matriz.proxima_revision)})}
          </span>
          <PorQue
            label={t('ipevr.porQue')}
            ariaLabel={t('ipevr.tipLabel.revisionVencida')}
            testId="ipevr-revision-vencida-porque"
            contenidoTestId="ipevr-revision-vencida-porque-contenido">
            {t('ipevr.revision.overduePorQue')}
          </PorQue>
        </div>
      )}

      <div className="surface flex flex-wrap items-center justify-between gap-x-4 gap-y-3 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
            {t('ipevr.metodologia.label')}
          </span>
          <div className="flex gap-0.5 rounded-lg bg-secondary p-0.5" role="group">
            {(['gtc_45', 'otra'] as const).map((option) => (
              <button
                key={option}
                type="button"
                disabled={!canEdit}
                aria-pressed={matriz.metodologia === option}
                onClick={() => setMetodologia.mutate(option)}
                data-testid={`ipevr-metodologia-${option}`}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                  matriz.metodologia === option
                    ? 'bg-card font-semibold text-primary shadow-xs'
                    : 'text-muted-foreground hover:text-foreground'
                )}>
                {t(`ipevr.metodologia.${option}`)}
              </button>
            ))}
          </div>
          <span className="inline-flex items-center gap-1">
            <Tag tone={matriz.estado === 'vigente' ? 'green' : 'amber'} data-testid="ipevr-estado">
              {t(`ipevr.estado.${matriz.estado}`)}
            </Tag>
            <HelpIcon label={t('ipevr.tipLabel.estado')} testId="ipevr-estado-help">
              {t('ipevr.estado.tip')}
            </HelpIcon>
          </span>
          {matriz.needs_reconfirmation && (
            <Tag tone="amber" data-testid="ipevr-needs-reconfirmation">
              {t('ipevr.gate.needsReconfirmation')}
            </Tag>
          )}
          {matriz.controles_sin_evidencia > 0 && (
            <button
              type="button"
              onClick={toggleSinEvidenciaFilter}
              aria-pressed={sinEvidenciaFilter}
              data-testid="ipevr-controles-sin-evidencia"
              className={`rounded-full border px-2 py-0.5 text-xs ${sinEvidenciaFilter ? 'border-primary bg-accent text-accent-foreground' : 'tone-amber border-input'}`}>
              {t('ipevr.controles.sinEvidenciaContador', {count: matriz.controles_sin_evidencia})}
            </button>
          )}
          {filasSinCalificar > 0 && (
            <button
              type="button"
              onClick={toggleSinCalificarFilter}
              aria-pressed={sinCalificarFilter}
              data-testid="ipevr-filas-sin-calificar"
              className={`rounded-full border px-2 py-0.5 text-xs ${sinCalificarFilter ? 'border-primary bg-accent text-accent-foreground' : 'tone-amber border-input'}`}>
              {t('ipevr.grid.sinCalificarContador', {count: filasSinCalificar})}
            </button>
          )}
          {/* num_expuestos only counts completed entrevistas; this N-of-M chip explains the gap. */}
          {matriz.entrevistas_completadas < matriz.empleados_activos && (
            <span className="inline-flex items-center gap-1">
              <span className="tone-amber rounded-full px-2 py-0.5 text-xs" data-testid="ipevr-cobertura-entrevistas">
                {t('ipevr.exposicion.coberturaContador', {
                  count: matriz.entrevistas_completadas,
                  total: matriz.empleados_activos,
                })}
              </span>
              <PorQue
                label={t('ipevr.porQue')}
                ariaLabel={t('ipevr.tipLabel.cobertura')}
                testId="ipevr-cobertura-porque"
                contenidoTestId="ipevr-cobertura-porque-contenido">
                {t('ipevr.exposicion.coberturaPorQue')}
              </PorQue>
            </span>
          )}
          {/* Reconciliation runs out of band; `useMatrizRiesgos` polls until it lands. */}
          {matriz.reconciliacion_en_curso && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-input px-2 py-0.5 text-xs text-muted-foreground"
              data-testid="ipevr-reconciliacion-en-curso">
              <span className="size-1.5 animate-pulse rounded-full bg-primary" aria-hidden="true" />
              {t('ipevr.exposicion.consolidando')}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {matriz.ultima_revision && (
            <span className="text-xs text-muted-foreground" data-testid="ipevr-ultima-revision">
              {t('ipevr.revision.last', {date: formatDateOnly(matriz.ultima_revision)})}
            </span>
          )}
          {canOperate && (
            <Button
              size="sm"
              variant="outline"
              disabled={markRevision.isPending || !canEdit}
              onClick={() => setConfirmingRevision(true)}
              data-testid="ipevr-mark-revision">
              {t('ipevr.revision.mark')}
            </Button>
          )}
          {canOperate && (
            <Button
              size="sm"
              variant="outline"
              disabled={reSolicitarTodas.isPending || !canEdit}
              onClick={() => setConfirmingResolicitarTodas(true)}
              data-testid="ipevr-resolicitar-todas">
              {t('ipevr.resolicitar.markTodas')}
            </Button>
          )}
          {(!isSystemUser || matriz.mark_vigente_dev_override) && canOperate && (
            <Button
              size="sm"
              disabled={
                markVigente.isPending ||
                (!matriz.can_mark_vigente && !matriz.mark_vigente_dev_override) ||
                matriz.estado === 'vigente'
              }
              onClick={() => markVigente.mutate(undefined, {onError: (err) => setVigenteError(translateApiError(err))})}
              data-testid="ipevr-mark-vigente">
              {t('ipevr.gate.markVigente')}
            </Button>
          )}
        </div>
      </div>

      {/* La puerta es estado persistente: va al slot de avisos, nunca a una burbuja,
          que es solo para el resultado de un clic (`lib/avisos.ts`). */}
      <PageNotices
        items={[
          !matriz.can_mark_vigente &&
            matriz.estado !== 'vigente' && {
              id: 'ipevr-gate',
              severidad: 'amber' as const,
              nodo: (
                <div className="notice-amber space-y-1 rounded-lg px-4 py-3" data-testid="ipevr-gate-banner">
                  <p className="text-sm">{gateBlockedCopy(t, matriz).banner}</p>
                  <PorQue
                    label={t('ipevr.porQue')}
                    ariaLabel={t('ipevr.tipLabel.gate')}
                    testId="ipevr-gate-porque"
                    contenidoTestId="ipevr-gate-porque-contenido">
                    {gateBlockedCopy(t, matriz).porque}
                  </PorQue>
                </div>
              ),
            },
        ]}
      />
      {vigenteError && (
        <p className="text-sm text-destructive" data-testid="ipevr-vigente-error">
          {vigenteError}
        </p>
      )}

      {/* Visible también con metodologia=otra: así se llega al estado vacío del mapa. */}
      <div className="surface flex flex-wrap items-center gap-2 px-4 py-2.5">
        <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('ipevr.agrupar.label')}
        </span>
        <div className="min-w-0 overflow-x-auto rounded-lg bg-secondary p-0.5">
          <div className="flex gap-0.5" role="group">
            {VISTAS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setGroupMode(option)
                  setPage(1)
                }}
                aria-pressed={groupMode === option}
                data-testid={`ipevr-agrupar-${option}`}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs whitespace-nowrap transition-colors',
                  groupMode === option
                    ? 'bg-card font-semibold text-primary shadow-xs'
                    : 'text-muted-foreground hover:text-foreground'
                )}>
                {t(`ipevr.agrupar.${option}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {groupMode === 'mapa' ? (
        <MapaCalorView
          businessId={businessId}
          matriz={matriz}
          onVerPendientes={() => {
            setGroupMode('none')
            setSinCalificarFilter(true)
            setPage(1)
          }}
        />
      ) : matriz.metodologia === 'otra' ? (
        <OtraMetodologiaPanel businessId={businessId} matriz={matriz} canEdit={canEdit} />
      ) : (
        <>
          <MatrizRiesgosToolbar
            search={search}
            onSearch={updateSearch}
            riskFilter={riskFilter}
            onToggleRisk={(v) => toggleInSet(setRiskFilter, v)}
            noAceptablePrimero={noAceptablePrimero}
            onToggleNoAceptablePrimero={updateNoAceptablePrimero}
            canEdit={canEdit}
            onAddFila={() => setAddingFila(true)}
            onImportar={() => setImportando(true)}
            clasificacionFilter={clasificacionFilter}
            onToggleClasificacion={(v) => toggleInSet(setClasificacionFilter, v)}
            estadoFilter={estadoFilter}
            onToggleEstado={(v) => toggleInSet(setEstadoFilter, v)}
            moreFiltersOpen={moreFiltersOpen}
            onToggleMoreFilters={() => setShowMoreFilters((v) => !v)}
            empleadoOptions={empleadoOptions}
            empleadoFilter={empleadoFilter}
            onChangeEmpleadoFilter={(next) => {
              setEmpleadoFilter(next)
              setPage(1)
            }}
            cargoOptions={cargoOptions}
            cargoFilter={cargoFilter}
            onChangeCargoFilter={(next) => {
              setCargoFilter(next)
              setPage(1)
            }}
            areaOptions={areaOptions}
            areaFilter={areaFilter}
            onChangeAreaFilter={(next) => {
              setAreaFilter(next)
              setPage(1)
            }}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
          />

          {visible.length === 0 ? (
            <div className="surface" data-testid="ipevr-grid-empty">
              <ListEmptyState
                title={hasActiveFilters ? t('ipevr.filter.empty') : t('ipevr.grid.empty')}
                action={
                  hasActiveFilters ? (
                    <Button size="sm" variant="outline" onClick={clearFilters} data-testid="ipevr-empty-clear-filters">
                      {t('ipevr.filter.clear')}
                    </Button>
                  ) : (
                    <Button asChild size="sm" data-testid="ipevr-grid-empty-cta">
                      {/* `/profile` y no `/perfil-riesgo`, que es solo un redirect heredado. */}
                      <Link to={`/businesses/${businessId}/profile`}>{t('ipevr.grid.emptyCta')}</Link>
                    </Button>
                  )
                }
              />
              {!hasActiveFilters && (
                <div className="flex justify-center px-6 pb-6 text-sm">
                  <PorQue
                    label={t('ipevr.deDondeSalen')}
                    testId="ipevr-grid-empty-porque"
                    contenidoTestId="ipevr-grid-empty-porque-contenido">
                    {t('ipevr.grid.emptyPorQue')}
                  </PorQue>
                </div>
              )}
            </div>
          ) : groupMode === 'none' ? (
            <>
              <div className="hidden md:block">
                <DataTable
                  columns={columns}
                  rows={paged}
                  rowKey={(f) => f.id}
                  rowTestId={() => 'ipevr-fila-row'}
                  rowHighlightId={(f) => `fila-${f.id}`}
                  onRowClick={setDetailsFila}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              </div>
              <ul className="surface md:hidden">
                {paged.map((f) => (
                  <FilaCard key={f.id} fila={f} onClick={() => setDetailsFila(f)} t={t} />
                ))}
              </ul>
              <PaginationBar page={page} pageSize={PAGE_SIZE} total={visible.length} onPageChange={setPage} />
            </>
          ) : (
            <GroupedMatriz
              filas={filtered}
              mode={groupMode}
              areas={matriz.areas}
              businessId={businessId}
              columns={columns}
              rowTestId={() => 'ipevr-fila-row'}
              onRowClick={setDetailsFila}
              noAceptablePrimero={noAceptablePrimero}
              renderMobileCard={(fila) => <FilaCard fila={fila} onClick={() => setDetailsFila(fila)} t={t} />}
            />
          )}
        </>
      )}

      <AddFilaDialog businessId={businessId} matriz={matriz} open={addingFila} onOpenChange={setAddingFila} />
      <ImportarMatrizDialog
        businessId={businessId}
        puedeConfirmar={matriz.can_mark_vigente || matriz.mark_vigente_dev_override}
        open={importando}
        onOpenChange={setImportando}
      />
      <EditFilaDialog
        businessId={businessId}
        matriz={matriz}
        // Re-resolved from the fresh list: a control changed mid-dialog makes the click-time snapshot stale.
        fila={editingFila ? (matriz.filas.find((f) => f.id === editingFila.id) ?? editingFila) : null}
        onClose={() => setEditingFila(null)}
      />
      <FilaDetailsDialog
        businessId={businessId}
        matriz={matriz}
        // Re-resolved from the fresh list so an action inside the dialog shows up at once.
        fila={detailsFila ? (matriz.filas.find((f) => f.id === detailsFila.id) ?? detailsFila) : null}
        canEdit={canEdit}
        onClose={() => setDetailsFila(null)}
        onEdit={(fila) => {
          setDetailsFila(null)
          setEditingFila(fila)
        }}
      />
      <ConfirmDialog
        open={confirmingRevision}
        onOpenChange={setConfirmingRevision}
        title={t('ipevr.revision.confirmTitle')}
        description={t('ipevr.revision.confirmBody')}
        confirmLabel={t('ipevr.revision.confirmAction')}
        cancelLabel={t('common.cancel')}
        pending={markRevision.isPending}
        onConfirm={() => markRevision.mutate(undefined, {onSuccess: () => setConfirmingRevision(false)})}
      />
      <ConfirmDialog
        open={confirmingResolicitarTodas}
        onOpenChange={setConfirmingResolicitarTodas}
        title={t('ipevr.resolicitar.confirmTitleTodas')}
        description={t('ipevr.resolicitar.confirmBodyTodas')}
        confirmLabel={t('ipevr.resolicitar.confirmAction')}
        cancelLabel={t('common.cancel')}
        pending={reSolicitarTodas.isPending}
        onConfirm={() =>
          reSolicitarTodas.mutate(undefined, {
            onSuccess: (result) => {
              setConfirmingResolicitarTodas(false)
              publicarExito(
                t('ipevr.resolicitar.resultTitulo'),
                t('ipevr.resolicitar.resultBody', {
                  reSolicitadas: result.re_solicitadas,
                  omitidas: result.omitidas,
                }),
                {testId: 'ipevr-resolicitar-result'}
              )
            },
          })
        }
      />
    </div>
  )
}
